'use client';

import AddIcon from '@mui/icons-material/Add';
import RemoveIcon from '@mui/icons-material/Remove';
import { Accordion, AccordionDetails, AccordionSummary, Grid2, Stack, Typography } from '@mui/material';
import Image from 'next/image';
import { useRouter } from 'next/navigation';
import { useState } from 'react';
import Marquee from 'react-fast-marquee';

import { icons } from '@/assets/icons';
import { partners } from '@/assets/partners';
import { CORE_ITEMS, FAQ_ITEMS_SHORT } from '@/global/constants/common';

import { CoreLaunchPadCard } from './bases/CoreLaunchPadCard';
import HexagonalButton from './bases/HexagonalButton';

export const FAQsSection = () => {
  const router = useRouter();
  const [expanded, setExpanded] = useState<string | false>(false);

  const handleChange = (panel: string) => (_event: React.SyntheticEvent, isExpanded: boolean) => {
    setExpanded(isExpanded ? panel : false);
  };

  return (
    <Stack
      width={'100%'}
      height={'100%'}
      flexGrow={1}
      alignItems={'center'}
      component={'section'}
      position={'relative'}>
      <Stack
        alignItems={'center'}
        padding={{ xs: '40px 24px', md: '80px' }}
        sx={{ borderBottom: '1px solid #FFFFFF33' }}>
        <Stack gap={{ xs: 3, md: 7 }} width={'min(1280px, 100%)'} zIndex={2}>
          <Typography
            component={'h2'}
            variant={'displayMedium'}
            className={'clip-text'}
            textAlign={'center'}
            fontSize={{ xs: '24px', md: '48px' }}
            lineHeight={{ xs: '32px', md: '68px' }}
            sx={{ background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)' }}>
            WHY LFG CORE LAUNCHPAD?
          </Typography>
          <Grid2 container spacing={{ xs: 2, md: 4 }}>
            {CORE_ITEMS.map(item => (
              <Grid2 size={{ xs: 6, md: 4 }} key={item.id}>
                <CoreLaunchPadCard item={item} />
              </Grid2>
            ))}
          </Grid2>
        </Stack>
      </Stack>
      <Stack padding={{ xs: '40px 24px', md: '80px' }} alignItems={'center'}>
        <Stack gap={{ xs: 4, md: 7 }} width={'min(1280px, 100%)'} zIndex={2} alignItems={'center'}>
          <Typography
            component={'h2'}
            variant={'displayMedium'}
            className={'clip-text'}
            textAlign={'center'}
            whiteSpace={'pre-line'}
            fontSize={{ xs: '24px', md: '48px' }}
            lineHeight={{ xs: '32px', md: '68px' }}
            sx={{ background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)' }}>
            ANSWERS TO YOUR
            {'\n'}
            <span style={{ WebkitTextFillColor: '#FF5319' }}>QUESTIONS</span>
          </Typography>
          <Stack width={'min(838px, 100%)'}>
            {FAQ_ITEMS_SHORT.map((item, index) => {
              const panel = `panel${index + 1}`;
              return (
                <Accordion
                  key={index.toString()}
                  expanded={expanded === panel}
                  onChange={handleChange(panel)}
                  sx={{
                    '&.MuiAccordion-root': {
                      borderRadius: 0,
                      backgroundColor: 'transparent',
                      padding: { xs: '24px 0px', md: '40px 0px' },
                      borderBottom: '1px solid #FFFFFF33',
                      '&.Mui-expanded': {
                        margin: 0,
                      },
                    },
                  }}>
                  <AccordionSummary
                    expandIcon={
                      expanded === panel ? (
                        <RemoveIcon style={{ color: '#FFF' }} />
                      ) : (
                        <AddIcon style={{ color: '#FFF' }} />
                      )
                    }
                    sx={{
                      '&.MuiAccordionSummary-root': {
                        padding: 0,
                      },
                    }}>
                    <Typography
                      fontSize={{ xs: '18px', md: '24px' }}
                      component={'span'}
                      variant={'headLarge2'}
                      color={'#FFF'}>{`${index + 1}. ${item.question}`}</Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Typography
                      fontSize={{ xs: '14px', md: '18px' }}
                      variant={'bodyLarge'}
                      color={'#FFFFFF'}
                      className={'mix-blend'}>
                      <div dangerouslySetInnerHTML={{ __html: item.answer }} />
                    </Typography>
                  </AccordionDetails>
                </Accordion>
              );
            })}
          </Stack>
          <Stack gap={3} width={'100%'} alignItems={'center'}>
            <Typography
              component={'h4'}
              textAlign={'center'}
              variant={'bodyLarge'}
              color={'#FFFFFF'}
              fontSize={{ xs: '14px', md: '18px' }}
              className={'mix-blend'}>
              Still have questions?
            </Typography>
            <Stack width={180} display={{ xs: 'none', md: 'block' }}>
              <HexagonalButton variant={'primary'} label={'VISIT FAQ'} onClick={() => router.push('/faq')} />
            </Stack>
            <Stack width={140} display={{ xs: 'block', md: 'none' }}>
              <HexagonalButton
                variant={'primary'}
                size={'small'}
                label={'VISIT FAQ'}
                onClick={() => router.push('/faq')}
              />
            </Stack>
          </Stack>
        </Stack>
      </Stack>
      <Stack width={'100%'} alignItems={'center'} paddingY={{ xs: '40px', md: '80px' }}>
        <Stack
          gap={7}
          width={'min(1280px, 100%)'}
          zIndex={2}
          marginBottom={{ xs: '20px', md: '40px' }}
          paddingX={{ xs: '24px', md: 0 }}>
          <Typography
            component={'h2'}
            variant={'displayMedium'}
            className={'clip-text'}
            textAlign={'center'}
            fontSize={{ xs: '24px', md: '48px' }}
            lineHeight={{ xs: '32px', md: '68px' }}
            sx={{ background: 'linear-gradient(90deg, #A9A9A9 0%, #FFFFFF 50%, #A9A9A9 100%)' }}>
            OUR PARTNERS
          </Typography>
        </Stack>
        <Marquee autoFill>
          {/* <Stack padding={{ xs: '20px 40px', md: '40px 80px' }}>
            <Image
              width={300}
              height={80}
              alt={''}
              src={partners.datagram_logo}
              className={'w-full h-[40px] object-contain md:h-[80px]'}
            />
          </Stack> */}
          <Stack padding={{ xs: '20px 40px', md: '40px 80px' }}>
            <Image
              width={300}
              height={80}
              alt={''}
              src={partners.helix_logo}
              className={'w-full h-[40px] object-contain md:h-[80px]'}
            />
          </Stack>
          <Stack padding={{ xs: '20px 40px', md: '40px 80px' }}>
            <Image
              width={300}
              height={80}
              alt={''}
              src={partners.unstoppable_domains_logo}
              className={'w-full h-[40px] object-contain md:h-[80px]'}
            />
          </Stack>
        </Marquee>
      </Stack>
      <Stack zIndex={1} position={'absolute'} top={'56%'} left={0} right={0} bottom={0}>
        <Image src={icons.ic_wave_section} alt={''} layout={'fill'} objectFit={'cover'} />
      </Stack>
    </Stack>
  );
};
